import { secondsToMilliseconds } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

import { DURATION } from '@/constants/animation';
import { ONE_SECOND } from '@/constants/time';

/** How long the content must go without a layout change to count as settled. */
const SETTLE_QUIET_MS = secondsToMilliseconds(0.15);

/**
 * The longest the cover waits for the layout to settle once loading ends. A message with an
 * image can keep reporting new sizes as it loads, and the reader should not be held behind a
 * placeholder for it.
 */
const SETTLE_MAX_MS = ONE_SECOND;

/** React Native's native driver cannot run under react-native-web; opacity there stays on JS. */
const USE_NATIVE_DRIVER = Platform.OS !== 'web';

type RevealWhenSettled = {
  /** Whether the cover is on screen, fading or not. */
  isCovered: boolean;
  /** The cover's opacity, 1 until it fades out. */
  coverOpacity: Animated.Value;
  /** The cover is fading out, so it should no longer take touches. */
  isFading: boolean;
  /** Call on every layout change of the content under the cover, such as a content size change. */
  notifyLayoutChange: () => void;
};

/**
 * Holds a cover over content while it loads, then fades it out once the content's layout has
 * settled, so the reader sees the finished screen appear rather than watch it lay itself out
 * and scroll into place.
 *
 * Settled means loading has ended and `notifyLayoutChange` has gone quiet for a moment, or the
 * cap has passed, whichever comes first. Content that was ready on mount is never covered.
 *
 * Built on core `Animated`, as `useCrossfade` is, so the fade runs on the native driver and
 * its end can unmount the cover through the animation's own completion callback.
 *
 * Under reduced motion the cover goes the moment the layout settles, without the fade.
 */
export function useRevealWhenSettled(isLoading: boolean): RevealWhenSettled {
  const [isCovered, setIsCovered] = useState(isLoading);

  const [isFading, setIsFading] = useState(false);

  const coverOpacity = useRef(new Animated.Value(1)).current;

  const reduceMotion = useReducedMotion();

  const quietTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const capTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isWaitingToSettle = useRef(false);

  const clearTimers = useCallback(() => {
    if (quietTimer.current) {
      clearTimeout(quietTimer.current);

      quietTimer.current = null;
    }

    if (capTimer.current) {
      clearTimeout(capTimer.current);

      capTimer.current = null;
    }
  }, []);

  const reveal = useCallback(() => {
    if (!isWaitingToSettle.current) {
      return;
    }

    isWaitingToSettle.current = false;

    clearTimers();

    if (reduceMotion) {
      setIsCovered(false);

      return;
    }

    setIsFading(true);

    Animated.timing(coverOpacity, {
      toValue: 0,
      duration: DURATION.normal,
      useNativeDriver: USE_NATIVE_DRIVER,
    }).start(({ finished }) => {
      if (!finished) {
        return;
      }

      setIsCovered(false);

      setIsFading(false);
    });
  }, [clearTimers, coverOpacity, reduceMotion]);

  const restartQuietTimer = useCallback(() => {
    if (quietTimer.current) {
      clearTimeout(quietTimer.current);
    }

    quietTimer.current = setTimeout(reveal, SETTLE_QUIET_MS);
  }, [reveal]);

  useEffect(() => {
    if (isLoading) {
      isWaitingToSettle.current = false;

      clearTimers();

      coverOpacity.stopAnimation();

      coverOpacity.setValue(1);

      setIsFading(false);

      setIsCovered(true);

      return;
    }

    if (!isCovered || isWaitingToSettle.current || isFading) {
      return;
    }

    isWaitingToSettle.current = true;

    restartQuietTimer();

    capTimer.current = setTimeout(reveal, SETTLE_MAX_MS);
  }, [clearTimers, coverOpacity, isCovered, isFading, isLoading, restartQuietTimer, reveal]);

  useEffect(() => clearTimers, [clearTimers]);

  const notifyLayoutChange = useCallback(() => {
    if (isWaitingToSettle.current) {
      restartQuietTimer();
    }
  }, [restartQuietTimer]);

  return { isCovered, coverOpacity, isFading, notifyLayoutChange };
}
